/**
 * Input Sanitization Utilities
 *
 * This module provides sanitization functions to prevent XSS attacks
 * by cleaning user-provided input before rendering or storing.
 *
 * Uses DOMPurify for robust HTML sanitization.
 */

import DOMPurify from 'dompurify';

/**
 * Configuration for DOMPurify
 * This restricts which HTML tags and attributes are allowed
 */
const SANITIZE_CONFIG = {
  ALLOWED_TAGS: [
    'b', 'i', 'em', 'strong', 'a', 'p', 'br', 'ul', 'ol', 'li',
    'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'blockquote', 'code', 'pre'
  ],
  ALLOWED_ATTR: ['href', 'title', 'target', 'rel'],
  ALLOW_DATA_ATTR: false,
  ALLOW_UNKNOWN_PROTOCOLS: false,
};

/**
 * Strict configuration for text-only content
 * Strips all HTML tags
 */
const STRICT_CONFIG = {
  ALLOWED_TAGS: [],
  ALLOWED_ATTR: [],
  KEEP_CONTENT: true, // Keep text content, strip tags
};

/**
 * Sanitizes HTML content to prevent XSS attacks
 *
 * Use this when you need to render user-provided HTML content
 * (e.g., rich text editor output, markdown converted to HTML)
 *
 * @param dirty - Potentially unsafe HTML string
 * @returns Sanitized HTML string safe for rendering
 *
 * @example
 * const userInput = '<script>alert("XSS")</script><p>Safe content</p>';
 * const safe = sanitizeHTML(userInput);
 * // Returns: '<p>Safe content</p>'
 */
export function sanitizeHTML(dirty: string): string {
  if (!dirty || typeof dirty !== 'string') {
    return '';
  }
  return DOMPurify.sanitize(dirty, SANITIZE_CONFIG);
}

/**
 * Sanitizes input to plain text only
 *
 * Use this for user input fields that should never contain HTML
 * (e.g., names, email addresses, short text fields)
 *
 * @param dirty - Potentially unsafe string
 * @returns Plain text with all HTML tags removed
 *
 * @example
 * const userInput = '<script>alert("XSS")</script>John Doe';
 * const safe = sanitizeText(userInput);
 * // Returns: 'John Doe'
 */
export function sanitizeText(dirty: string): string {
  if (!dirty || typeof dirty !== 'string') {
    return '';
  }
  return DOMPurify.sanitize(dirty, STRICT_CONFIG);
}

/**
 * Sanitizes a URL to prevent javascript: or data: URIs
 *
 * @param url - Potentially unsafe URL
 * @returns Sanitized URL or empty string if invalid
 *
 * @example
 * sanitizeURL('javascript:alert(1)'); // Returns: ''
 * sanitizeURL('https://example.com'); // Returns: 'https://example.com'
 */
export function sanitizeURL(url: string): string {
  if (!url || typeof url !== 'string') {
    return '';
  }

  const trimmed = url.trim();

  // Block dangerous protocols
  const dangerousProtocols = [
    'javascript:',
    'data:',
    'vbscript:',
    'file:',
    'about:',
  ];

  const lowerURL = trimmed.toLowerCase();
  for (const protocol of dangerousProtocols) {
    if (lowerURL.startsWith(protocol)) {
      console.warn('[Security] Blocked dangerous URL protocol:', protocol);
      return '';
    }
  }

  // Allow only http, https, mailto, tel
  try {
    const parsed = new URL(trimmed);
    const allowedProtocols = ['http:', 'https:', 'mailto:', 'tel:'];

    if (!allowedProtocols.includes(parsed.protocol)) {
      console.warn('[Security] Blocked non-whitelisted URL protocol:', parsed.protocol);
      return '';
    }

    return trimmed;
  } catch {
    // If it's not a valid absolute URL, check if it's a relative URL
    if (trimmed.startsWith('/') || trimmed.startsWith('./') || trimmed.startsWith('../')) {
      return trimmed;
    }
    console.warn('[Security] Invalid URL format:', trimmed);
    return '';
  }
}

/**
 * Sanitizes an email address
 *
 * @param email - Potentially unsafe email
 * @returns Sanitized email or empty string if invalid
 */
export function sanitizeEmail(email: string): string {
  if (!email || typeof email !== 'string') {
    return '';
  }

  const trimmed = email.trim();

  // Basic email validation regex
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  if (!emailRegex.test(trimmed)) {
    return '';
  }

  // Remove any HTML tags
  return sanitizeText(trimmed);
}

/**
 * Sanitizes a phone number
 *
 * @param phone - Potentially unsafe phone number
 * @returns Sanitized phone number with only digits, spaces, +, -, (, )
 */
export function sanitizePhone(phone: string): string {
  if (!phone || typeof phone !== 'string') {
    return '';
  }

  // Allow only digits, spaces, +, -, (, )
  return phone.replace(/[^\d\s+\-()]/g, '').trim();
}

/**
 * Sanitizes an object by sanitizing all string values
 *
 * Use this for sanitizing form data before storage or submission
 *
 * @param obj - Object with potentially unsafe strings
 * @param mode - 'text' for plain text, 'html' for HTML content
 * @returns Object with sanitized string values
 */
export function sanitizeObject<T extends Record<string, any>>(
  obj: T,
  mode: 'text' | 'html' = 'text'
): T {
  const sanitized = { ...obj } as any;

  for (const key in sanitized) {
    const value = sanitized[key];

    if (typeof value === 'string') {
      sanitized[key] = mode === 'html' ? sanitizeHTML(value) : sanitizeText(value);
    } else if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
      sanitized[key] = sanitizeObject(value, mode);
    } else if (Array.isArray(value)) {
      sanitized[key] = value.map(item =>
        typeof item === 'string'
          ? mode === 'html' ? sanitizeHTML(item) : sanitizeText(item)
          : typeof item === 'object' ? sanitizeObject(item, mode) : item
      );
    }
  }

  return sanitized as T;
}

/**
 * Validates and sanitizes file names
 *
 * @param filename - Potentially unsafe filename
 * @returns Sanitized filename or empty string if invalid
 */
export function sanitizeFilename(filename: string): string {
  if (!filename || typeof filename !== 'string') {
    return '';
  }

  // Remove path traversal attempts
  let safe = filename.replace(/\.\./g, '');

  // Remove null bytes
  safe = safe.replace(/\0/g, '');

  // Allow only alphanumeric, dash, underscore, dot
  safe = safe.replace(/[^a-zA-Z0-9._-]/g, '_');

  // Prevent hidden files
  if (safe.startsWith('.')) {
    safe = safe.substring(1);
  }

  // Limit length
  if (safe.length > 255) {
    safe = safe.substring(0, 255);
  }

  return safe;
}

/**
 * Creates a secure random string for tokens or IDs
 *
 * @param length - Length of the random string (default: 32)
 * @returns Cryptographically secure random string
 */
export function generateSecureToken(length: number = 32): string {
  const array = new Uint8Array(length);
  crypto.getRandomValues(array);
  return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
}
